/**
 * ``ListFollowToggle`` — the list-panel header control for the per-panel
 * **"Follow live updates"** flag (REQ-UI-182; operator request 2026-10-01).
 *
 * One pressed-state icon button (``aria-pressed``; the annotation palette's
 * convention) in the header of each of the four operator list panels —
 * Platforms, Detections, Alerts, Tracks. Pressed (a filled pin) = the panel
 * follows: with nothing selected the newest rows stay in view. Released (a
 * slashed pin) = the panel is **paused** — the rows on screen are the ones
 * that were there at the flip, so they can be read without the list moving
 * (:func:`@/multi-pane/use-paused-list-rows`; REQ-UI-182 amended 2026-10-10),
 * and the chip beside the button says so and counts what is withheld. A
 * selected row is held in place while paused; pressing the toggle again
 * resumes and returns to the top of the list. The flag itself lives in
 * :mod:`./list-follow-store` (session-only) and is consumed by
 * :func:`@/selection/use-keep-selected-row-in-view`.
 */
import { PushPin, PushPinSlash } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import {
  type FollowablePanelId,
  useListFollow,
  useListFollowStore,
} from "@/multi-pane/list-follow-store";

/** The accessible name of the toggle — the same in both states. */
export const FOLLOW_TOGGLE_LABEL = "Follow live updates";

/** Props for :func:`ListFollowToggle`. */
export interface ListFollowToggleProps {
  /** Which list panel's flag this button controls. */
  readonly panelId: FollowablePanelId;
  /**
   * How many live rows the paused list is not showing
   * (:attr:`@/multi-pane/use-paused-list-rows.PausedListRows.withheld`).
   * Rendered only while paused; omit in a context that has no row set.
   */
  readonly withheld?: number;
}

/** Above this the chip says ``999+`` rather than widening the header. */
const WITHHELD_DISPLAY_CAP = 999;

/**
 * The header toggle, plus the **Paused** chip that states what a released
 * toggle is holding back.
 *
 * Renders ``aria-pressed`` from the store and flips the flag on click; the
 * title explains the current state and what a click does. A paused list hides
 * rows the operator would otherwise be seeing, so the count is on screen and
 * in the button's title rather than implied by an icon — a paused list that
 * quietly withholds 1,200 detections is worse than no pause at all
 * (CLAUDE.md §8.1, operator visibility). The chip is plain text and
 * deliberately **not** a live region: under load the number changes several
 * times a second, which would make a screen reader unusable.
 */
export function ListFollowToggle({
  panelId,
  withheld = 0,
}: ListFollowToggleProps): React.ReactElement {
  const follow = useListFollow(panelId);
  const toggleFollow = useListFollowStore((state) => state.toggleFollow);
  const Icon = follow ? PushPin : PushPinSlash;
  // Short enough that a narrow panel header keeps its title on one line —
  // "PAUSED +384 NEW" wrapped "Detections 57" at a 280 px panel width.
  const withheldText =
    withheld <= 0
      ? ""
      : ` +${withheld > WITHHELD_DISPLAY_CAP ? `${WITHHELD_DISPLAY_CAP}+` : withheld}`;
  return (
    <div className="flex items-center gap-2">
      {!follow && (
        <span
          data-testid={`list-paused-chip-${panelId}`}
          className={cn(
            "inline-flex min-w-0 items-center truncate rounded border border-border px-1.5 py-0.5",
            "text-caption font-medium uppercase tracking-wide text-text-muted",
          )}
        >
          Paused{withheldText}
        </span>
      )}
      <button
        type="button"
        aria-pressed={follow}
        aria-label={FOLLOW_TOGGLE_LABEL}
        title={
          follow
            ? "Following live updates — the newest rows stay in view. Click to pause the list."
            : `Paused — the rows on screen stay put${
                withheld <= 0 ? "" : `, ${withheld} new row(s) waiting`
              }. Click to follow live updates again.`
        }
        data-testid={`list-follow-toggle-${panelId}`}
        onClick={() => toggleFollow(panelId)}
        className={cn(
          "flex h-6 w-6 shrink-0 items-center justify-center rounded text-text-secondary",
          "hover:bg-bg-elevated focus:outline-none focus-visible:ring-1 focus-visible:ring-brand",
          follow && "text-brand",
        )}
      >
        <Icon size={16} weight={follow ? "fill" : "regular"} aria-hidden />
      </button>
    </div>
  );
}
